import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/**
 * Opens a Stripe Customer Portal session for the authenticated patient.
 *
 * The portal is Stripe's hosted page where the patient can:
 *   - view invoice history + download receipts
 *   - update payment method
 *   - cancel a subscription (at period end or immediately, depending on
 *     the Dashboard config — see STRIPE_DEPLOYMENT.md → Customer Portal)
 *
 * Returns a react-query mutation. On success the patient is redirected
 * to the portal URL. Callers can choose to redirect in the same window
 * (default) or open in a new tab via `target`.
 *
 * Example:
 *   const portal = useBillingPortal();
 *   <Button loading={portal.isPending} onClick={() => portal.mutate()}>
 *     Manage subscription
 *   </Button>
 */
export function useBillingPortal(opts?: { target?: "_self" | "_blank"; returnUrl?: string }) {
  const target = opts?.target ?? "_self";

  return useMutation<string, Error, void>({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<{ url?: string; error?: string }>(
        "stripe-create-billing-portal-session",
        { body: opts?.returnUrl ? { return_url: opts.returnUrl } : {} },
      );
      if (error) throw new Error(error.message ?? "Failed to open billing portal");
      if (!data?.url) throw new Error(data?.error ?? "Stripe did not return a portal URL");
      return data.url;
    },
    onSuccess: (url) => {
      if (target === "_blank") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = url;
      }
    },
  });
}
